import React from "react";
import { ArrowLeft } from "lucide-react";

const PROVERB_HOSTS = new Set(["ineedaproverb.com", "www.ineedaproverb.com"]);
const PROVERB_FALLBACK = "https://www.ineedaproverb.com/";
const RETURN_URL_KEY = "lg_proverb_return";

/**
 * Read the persisted safe return URL for the current I-Need-a-Proverb session.
 * Only URLs pointing at ineedaproverb.com / www.ineedaproverb.com are honored;
 * anything else falls back to the site root. Never trusts URLs from localStorage
 * without re-validating.
 */
export function getSafeProverbReturnUrl() {
  try {
    const raw = localStorage.getItem(RETURN_URL_KEY);
    if (!raw) return PROVERB_FALLBACK;
    const url = new URL(raw);
    if (url.protocol !== "https:") return PROVERB_FALLBACK;
    if (!PROVERB_HOSTS.has(url.hostname)) return PROVERB_FALLBACK;
    return url.toString();
  } catch {
    return PROVERB_FALLBACK;
  }
}

/**
 * Persist a return URL for later use by ReturnToProverb. Silently ignores
 * unsafe values (wrong host, wrong scheme, malformed URL) so callers can
 * pipe raw query-param / JWT-payload strings through it without pre-validating.
 */
export function setSafeProverbReturnUrl(raw) {
  if (!raw || typeof raw !== "string") return;
  try {
    const url = new URL(raw);
    if (url.protocol !== "https:") return;
    if (!PROVERB_HOSTS.has(url.hostname)) return;
    localStorage.setItem(RETURN_URL_KEY, url.toString());
  } catch {
    /* noop */
  }
}

export function clearSafeProverbReturnUrl() {
  try {
    localStorage.removeItem(RETURN_URL_KEY);
  } catch {
    /* noop */
  }
}

/**
 * Small, deliberately non-competing secondary link that sends the visitor
 * back to the exact "I Need a Proverb" page they came from (when safely
 * available) or to ineedaproverb.com otherwise.
 *
 * The URL is validated against an allowlist of hostnames — no other host
 * can be linked through this component, even if someone tampers with
 * localStorage.
 */
export default function ReturnToProverb({
  className = "",
  testid = "return-to-proverb",
}) {
  const href = getSafeProverbReturnUrl();
  return (
    <a
      href={href}
      data-testid={testid}
      // Full-page navigation — leaving the LifeGuide SPA on purpose. No
      // target="_blank": browsers keep this in the same tab so the user's
      // Back button walks the history normally, in either direction.
      className={
        className ||
        "inline-flex items-center gap-1.5 text-sm text-[#6B6B6B] hover:text-[#4F6F52] transition"
      }
    >
      <ArrowLeft size={14} strokeWidth={2} />
      Return to I Need a Proverb
    </a>
  );
}
